import React, { useCallback, useMemo, useState } from 'react';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/temp-client-utils';
import { Mutation } from '@css-xp/apd-common';
import { useApplicationContext } from '@css-xp/base/common-context';
import { cancelRequest as CANCEL_REQUEST } from '../queries/tasks';

interface Props {
  businessKey?: string | null;
  processInstanceId?: string;
}

export const useCancelRequest = ({ businessKey, processInstanceId }: Props) => {
  const [cancelApprovalRequestProcessing, setCancelApprovalRequestProcessing] = useState<boolean>(false);
  const [cancelApprovalRequestError, setCancelApprovalRequestError] = useState<Error | null>(null);
  const client = useMemo(() => abgGraphqlClient(ProxyGraphClients.APD), []);

  const userProfile = useApplicationContext();
  const userID = userProfile?.userId?.split('@')?.[0];

  const cancelApprovalRequest = useCallback(
    async (comment: string) => {
      if (!businessKey && !processInstanceId) return;

      let isRequestCanceled = false;
      let error = null;
      const variables: any = { id: userID };
      if (businessKey) {
        variables.businessKey = businessKey;
      }
      if (processInstanceId) {
        variables.processInstanceId = processInstanceId;
      }
      if (comment) {
        variables.comment = comment;
      }
      try {
        setCancelApprovalRequestProcessing(true);
        const { data, errors } = await client.mutate<Mutation>({
          mutation: CANCEL_REQUEST,
          variables,
          fetchPolicy: 'no-cache',
        });

        const errorMsg = errors?.[0]?.extensions?.message;
        if (errorMsg) {
          throw new Error(errorMsg);
        }
        const status = data?.cancelRequest?.status;
        if (status === 'OK') {
          isRequestCanceled = true;
          error = null;
          setCancelApprovalRequestError(null);
        }
      } catch (e: any) {
        error = e;
        setCancelApprovalRequestError(e as Error);
      } finally {
        setCancelApprovalRequestProcessing(false);
      }
      return { isRequestCanceled, error };
    },
    [businessKey, processInstanceId, userProfile]
  );

  // below method is used for bulk cancelation only
  const submitRequestAfterApprovalCancelation = useCallback(
    async (callback: () => void, hasOpenApprovalRequest: boolean) => {
      if (!hasOpenApprovalRequest) {
        callback();
        return;
      }
      const comment = 'User has requested to cancel this open approval request.';
      const cancelRequestResponse = await cancelApprovalRequest(comment);
      if (cancelRequestResponse && !cancelRequestResponse.error && cancelRequestResponse.isRequestCanceled) {
        callback();
      }
    },
    [businessKey, cancelApprovalRequest, processInstanceId, userProfile]
  );

  return {
    cancelApprovalRequestProcessing,
    cancelApprovalRequest,
    cancelApprovalRequestError,
    submitRequestAfterApprovalCancelation,
  };
};
